import { useEffect, useState, useRef } from 'react';
import { ComboBox, Item, Key, Text } from '@adobe/react-spectrum';
import { DataComponentProps } from './DataComponentProps';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../contexts/DependencyProvider';
import { ListItemRequest } from '../../../services/soap/item/requests/ListItemRequest';

// Extended interface to handle FormControl usage
export interface IItemDropDownProps extends DataComponentProps {
  itemType?: string;
  status?: string;
  returnFullObject?: boolean;
  /** Called with the full item when returnFullObject is true; stores in separate structure. */
  onFullObjectChange?: (key: string, fullObject: Record<string, any> | null) => void;
  /** Optional filter that will be sent as the `type` field on ListItemRequest to filter items. */
  filterTypeId?: string;
}

// Interface for FormControl usage
interface ItemDropDownFormControlProps {
  component: any;
  updateData: (key: string, value: any) => void;
  getData: (key: string, default_value: any) => any;
  updateFullObject?: (key: string, fullObject: any) => void;
}

const ItemDropDown: React.FC<IItemDropDownProps> = ({
  compKey,
  label,
  width,
  isRequired,
  onValueChange,
  value: dvalue,
  onBlur,
  itemType,
  status,
  returnFullObject = false,
  onFullObjectChange,
  filterTypeId,
}) => {
  const { t } = useTranslation();
  const { itemService, store } = useDependency();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [value, setValue] = useState<Key | null>(null);
  const [input, setInput] = useState('');
  const [searchText] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load items data
  useEffect(() => {
    const loadItems = async () => {
      if (!isMounted.current) return;
      
      setLoading(true);
      
      try {
        const requestOptions: any = {
          offset: 0,
          limit: 200
        };

        if (filterTypeId && String(filterTypeId).trim() !== '') {
          requestOptions.type = String(filterTypeId).trim();
        }
        
        if (searchText.trim()) {
          requestOptions.searchName = searchText.trim();
        }

        const response = await itemService.listItem(
          new ListItemRequest(
            store.Server,
            store.SessionId,
            requestOptions.limit,
            requestOptions.offset,
            requestOptions
          )
        );

        if (!isMounted.current) return;

        let itemList: any[] = [];
        if (response.result === 'OK' && response?.count > 0) {
          if (Array.isArray(response.ITEM)) {
            itemList = response.ITEM;
          } else if (response.ITEM) {
            itemList = [response.ITEM];
          }
        }

                 setItems(itemList);
      } catch (err) {
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadItems();
  }, [itemService, store.Server, store.SessionId, searchText, filterTypeId]);

  useEffect(() => {
    const keyForDisplay =
      typeof dvalue === "object" && dvalue !== null && "id" in dvalue
        ? (dvalue as { id: string | number }).id
        : dvalue;
    setValue(keyForDisplay ? (keyForDisplay as Key) : null);
    if (dvalue === "-1") {
      setErrorMessage(message);
    }
  }, [dvalue, message]);

  // When items load and we have an initial value (e.g. from form load / global search), sync full object
  const lastHydratedValueRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && items.length > 0 && dvalue && dvalue !== "" && dvalue !== "-1" && returnFullObject && onFullObjectChange) {
      const keyForDisplay = typeof dvalue === "object" && dvalue !== null && "id" in dvalue ? (dvalue as { id: string | number }).id : dvalue;
      const valueId = String(keyForDisplay);
      if (lastHydratedValueRef.current === valueId) return; // already synced
      const match = items.find((i) => String(i.id) === valueId);
      if (match) {
        lastHydratedValueRef.current = valueId;
        onFullObjectChange(compKey, match);
      }
    } else if (!dvalue || dvalue === "" || dvalue === "-1") {
      lastHydratedValueRef.current = null;
    }
  }, [loading, items, dvalue, returnFullObject, onFullObjectChange, compKey]);

  const onSelectionChange = (selectedKey: Key | null) => {
    const idStr = selectedKey ? selectedKey.toString() : "";
    if (onValueChange) onValueChange(idStr, input);
    if (returnFullObject && onFullObjectChange) {
      const fullItem = selectedKey
        ? items.find((i) => String(i.id) === String(selectedKey)) ?? null
        : null;
      onFullObjectChange(compKey, fullItem);
    }
    setValue(selectedKey ?? null);
    if (isRequired && !selectedKey) setErrorMessage(message);
    else setErrorMessage("");
  };

  const onInputChange = (value: string) => {
    setInput(value ?? '');
    // setSearchText(value);
    if(isRequired && !value) 
      setErrorMessage(message);
    else 
      setErrorMessage('');
  }

  return (
    <ComboBox 
      key={compKey} 
      label={label} 
      width={width} 
      isRequired={isRequired} 
      selectedKey={value ?? null}
      onSelectionChange={onSelectionChange} 
      onInputChange={onInputChange}
      validationState={errorMessage ? 'invalid' : undefined}
      errorMessage={errorMessage}
      onBlur={onBlur}
      >
             {loading ? (
         <Item key="loading" textValue="Loading...">Loading...</Item>
       ) : (
         items && items.map((item: any) => {
           const displayText = `${item.name} (${item.refId})`;
           return (
             <Item key={item.id} textValue={displayText}>
               <Text>{displayText}</Text>
             </Item>
           );
         })
       )}
    </ComboBox>
  );
};

// Wrapper component for FormControl usage
export const ItemDropDownFormControl: React.FC<ItemDropDownFormControlProps> = ({
  component,
  updateData,
  getData,
  updateFullObject,
}) => {
  const handleValueChange = (value: string) => {
    updateData(component.FORMITEM, value);
  };

  //const returnFullObject = (component.PROPERTIES as any).returnFullObject === "true";
  const returnFullObject = true;

  return (
    <ItemDropDown
      compKey={component.FORMITEM}
      label={component.PROPERTIES.name || component.PROPERTIES.caption || "Item"}
      width={"100%"}
      value={getData(component.FORMITEM, "")}
      isRequired={component.PROPERTIES.required === "true"}
      onValueChange={handleValueChange}
      itemType={(component.PROPERTIES as any).itemType}
      status={(component.PROPERTIES as any).status}
      returnFullObject={returnFullObject}
      onFullObjectChange={returnFullObject && updateFullObject ? (key, full) => updateFullObject(key, full) : undefined}
      filterTypeId={(component.PROPERTIES as any).filterTypeId}
    />
  );
};

export default ItemDropDown; 